import { SortOrder } from 'models/SortOrder';
import { Action } from 'redux';
import { PageState } from 'models/PageState';
import { ListViewProduct } from 'models/ListViewProduct';
import { createHref, IHref } from 'components/Shared/server/href';
import { reduxDispatchEvent } from 'components/Shared/communication';
import { historyPushState } from 'store/history/actions';
import { FilterTypeParams } from 'components/Filter/actions';
import { debounceP } from 'utils/debounce';
import { getProductlist, getProducts, getSearch } from 'services/ApiProductlistService';
import { SideEffect, ThunkDispatch } from 'components/Shared/connect';
import { getPage } from 'components/Pagelayout/actions';
import { handleServerError } from 'components/Shared/server';
import { isProductlist, isNormalProductlistPage } from 'views/ProductlistPage/selectors';
import { ProductlistFilterResponse } from 'models/ProductlistFilterResponse';
import { ILocation } from 'models/ILocation';
import warning from 'warning';
import { isSearchProductlistPage } from 'models/ProductlistPageModel';
import { saveToSessionStorage, PRODUCTLIST_KEY } from 'utils';

export const GOTO_PAGE = 'GOTO_PAGE';
export const FETCH_PAGE_ERROR = 'FETCH_PAGE_ERROR';
export const SET_SORT_ORDER = 'SET_SORT_ORDER';
export const PRODUCTLIST_PRODUCTS_UPDATE = 'PRODUCTLIST_PRODUCTS_UPDATE';

export interface GotoPageAction extends Action {
  pageNumber: number;
  products: ListViewProduct[];
}

export interface SortOrderAction extends Action {
  sortOrder: SortOrder;
}

export type ProductlistFilterAction = Action & ProductlistFilterResponse;

const INTERVAL = 250;
const getProductlistD = debounceP(getProductlist, INTERVAL);
const getProductsD = debounceP(getProducts, INTERVAL);
const getSearchProductsD = debounceP(getSearch, INTERVAL);

export const fetchSortedPage =
  (sortOrder: SortOrder): SideEffect =>
  dispatch => {
    dispatch(setSortOrder(sortOrder));
    return dispatch(fetchPage({ page: 1, sortOrder }));
  };

export interface FetchPageArgs {
  scrollToTop?: boolean;
  moreProducts?: boolean;
  sortOrder?: SortOrder;
}

export const fetchNextPage =
  ({ moreProducts, scrollToTop }: FetchPageArgs = {}): SideEffect =>
  (dispatch, getState) => {
    const currentPage = getState().currentPage;
    if (!isProductlist(currentPage)) {
      warning(true, 'currentPage is not a productlistpage');
      return Promise.resolve();
    }
    const { selectedSortOrder: sortOrder, pageNbr } = currentPage;
    return dispatch(
      fetchPage({ page: pageNbr + 1, moreProducts, sortOrder, scrollToTop, forceAllowBack: true })
    );
  };

export const fetchPrevPage =
  ({ scrollToTop }: FetchPageArgs = {}): SideEffect =>
  (dispatch, getState) => {
    const currentPage = getState().currentPage;
    if (!isProductlist(currentPage)) {
      warning(true, 'currentPage is not a productlistpage');
      return Promise.resolve();
    }
    const { selectedSortOrder: sortOrder, pageNbr } = currentPage;
    return dispatch(fetchPage({ page: pageNbr - 1, sortOrder, scrollToTop, forceAllowBack: true }));
  };

interface FetchSpecificPageArgs extends FetchPageArgs {
  page?: number;
  emit?: boolean;
  forceAllowBack?: boolean;
}

export const fetchPage =
  ({
    page = 1,
    moreProducts,
    sortOrder,
    emit,
    forceAllowBack
  }: FetchSpecificPageArgs): SideEffect =>
  (dispatch, getState) => {
    const { location, currentPage } = getState();
    if (!isProductlist(currentPage)) {
      warning(true, 'currentPage is not a productlistpage');
      return Promise.resolve();
    }

    const { search, pathname } = cleanLocation(location, page, sortOrder, emit, forceAllowBack);

    if (isNormalProductlistPage(currentPage)) {
      const hasBrandFilter =
        currentPage.categoryFilterFacet !== undefined &&
        currentPage.categoryFilterName !== undefined;

      if (
        currentPage.isCategoryFilterPage &&
        hasBrandFilter &&
        search[currentPage.categoryFilterFacet] === undefined
      ) {
        search[currentPage.categoryFilterFacet] = currentPage.categoryFilterName;
      }
    }

    const order = sortOrder || currentPage.selectedSortOrder;

    const productsPromise = isSearchProductlistPage(currentPage)
      ? getSearchProductsD(currentPage.suggestedSearchPhrase || search.phrase, page, order, search)
      : getProductsD(currentPage.webCatalog.toLowerCase(), page, order, search);

    return productsPromise
      .then(newProducts => {
        const products = moreProducts ? [...currentPage.products, ...newProducts] : newProducts;

        if (moreProducts) {
          saveToSessionStorage(PRODUCTLIST_KEY, {
            pathname,
            page,
            products
          });
        }

        return dispatch(gotoPage(page, products));
      })
      .catch(handleServerError(dispatch));
  };

export const gotoPage = (pageNumber: number, products: ListViewProduct[]): GotoPageAction => ({
  type: GOTO_PAGE,
  pageNumber,
  products
});

export const setSortOrder = (sortOrder: SortOrder): SortOrderAction => ({
  type: SET_SORT_ORDER,
  sortOrder
});

export const filterChanged =
  (_args?: FilterTypeParams) => (dispatch: ThunkDispatch, getState: () => PageState) => {
    const { location, currentPage } = getState();
    if (!isProductlist(currentPage)) {
      warning(true, 'Page is not a productlist');
      return;
    }
    const pageNbr = 1;
    const { selectedSortOrder, webCatalog, orderedFilterAttributes } = currentPage;
    const newLocation = cleanLocation(location, pageNbr, selectedSortOrder);
    const fetchWholePage = currentPage.isCategoryFilterPage || isSearchProductlistPage(currentPage);

    if (fetchWholePage) {
      return dispatch(getPage(createHref(newLocation), { scrollTo: false }));
    }

    const filterOrder = orderedFilterAttributes ? orderedFilterAttributes.join(',') : '';

    return getProductlistD(
      webCatalog.toLowerCase(),
      filterOrder,
      pageNbr,
      selectedSortOrder,
      newLocation.search
    ).then(response =>
      dispatch({
        type: PRODUCTLIST_PRODUCTS_UPDATE,
        pageNbr,
        ...response
      })
    );
  };

const cleanLocation = (
  location: ILocation,
  page: number,
  sortOrder?: SortOrder,
  emit?: boolean,
  forceAllowBack?: boolean
): IHref => {
  const url = createHref(location);
  if (page === 1) {
    delete url.search.page;
  } else {
    url.search.page = page.toString();
  }

  if (sortOrder === SortOrder.RelevancyDesc) {
    delete url.search.order;
  } else if (sortOrder) {
    url.search.order = sortOrder.toString();
  }

  !emit && reduxDispatchEvent(historyPushState({ url, state: { forceAllowBack } }));
  return url;
};
